import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ROUTE_IDS, type RouteId } from './routes';

interface NavigationValue {
  route: RouteId;
  navigate: (route: RouteId) => void;
}

const NavigationContext = createContext<NavigationValue | null>(null);

function readInitialRoute(): RouteId {
  const hash = window.location.hash.replace(/^#\/?/, '');
  return (ROUTE_IDS as readonly string[]).includes(hash) ? (hash as RouteId) : 'home';
}

/**
 * Minimal in-memory router. The activity lives in a sandboxed iframe served
 * through Discord's proxy, so we deliberately avoid the History API and keep
 * navigation in state (mirrored to the hash for reloads during development).
 */
export function NavigationProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<RouteId>(readInitialRoute);

  const navigate = useCallback((next: RouteId) => {
    setRoute(next);
    window.location.hash = `/${next}`;
  }, []);

  const value = useMemo<NavigationValue>(() => ({ route, navigate }), [route, navigate]);

  return <NavigationContext value={value}>{children}</NavigationContext>;
}

export function useNavigation(): NavigationValue {
  const value = useContext(NavigationContext);
  if (!value) {
    throw new Error('useNavigation must be used inside a NavigationProvider');
  }
  return value;
}
